import React, { FC, Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { OrderBy, OrderDirection } from 'models/search-result';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import LabPageActions from 'components/labs/lab-page-actions/lab-page-actions.component';
import LabList from 'components/labs/lab-list/lab-list.component';
import AddLabDialog from 'components/labs/add-update-lab-dialog/add-lab-dialog.component';
import EditLabDialog from 'components/labs/add-update-lab-dialog/edit-lab-dialog.component';
import TransportSettingsDialog from 'components/labs/transport-settings-dialog/trasnport-settings-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import SyncHitHoriozonsDialog from 'components/shared/sync-hit-horiozons-dialog/sync-hit-horiozons-dialog.component';
import { labsForAdminTemplate } from 'apollo/templates/lab';
import { Lab } from 'models/lab';
import { Organization } from 'models/organization';
import { OrganizationListFilter } from 'models/common';
import PdfTemplateDialog from 'components/labs/pdf-template/pdf-template-dialog.component';

const AdminLabs: FC = () => {
    const { t: translate } = useTranslation(['navigation', 'organization']);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isTransportDialogOpen, setIsTransportDialogOpen] = useState(false);
    const [isHHSyncDialogOpen, setIsHHSyncDialogOpen] = useState(false);
    const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
    const [filter, setFilter] = useState<OrganizationListFilter>({ freeText: '', active: true });
    const [lastOffset, setLastOffset] = useState(0);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'name',
        orderDirection: OrderDirection.asc,
    });
    const [labs, setLabs] = useState([]);
    const [currentLab, setCurrentLab] = useState<Lab>();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [labsForAdmin, { loading, networkStatus, refetch, fetchMore }] = useLazyQuery(labsForAdminTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: filter,
            limit: 25,
            offset: 0,
            orderBy: orderBy.orderBy,
            orderDirection: orderBy.orderDirection,
        },
        onCompleted: ({ labsForAdmin }) => {
            setLastOffset(labsForAdmin.length);
            setLabs(labsForAdmin);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        labsForAdmin();
    }, []);

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: OrganizationListFilter) => {
            setLastOffset(0);
            setFilter({ freeText: freeText.toLocaleLowerCase(), active: active });
        }, 300),
        [],
    );

    const setOrderOnField = (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
        refetch({
            filters: filter,
            offset: 0,
            orderBy: field,
            orderDirection: direction,
        });
        setLastOffset(0);
    };

    const handleCloseDialog = () => {
        setIsAddDialogOpen(false);
        setIsEditDialogOpen(false);
        setIsTransportDialogOpen(false);
        setIsHHSyncDialogOpen(false);
        setIsTemplateDialogOpen(false);
    };

    const handleLabEditClick = (lab: Lab) => {
        setCurrentLab(lab);
        setIsEditDialogOpen(true);
    };

    const handleTransportSettingsClick = (lab: Lab) => {
        setCurrentLab(lab);
        setIsTransportDialogOpen(true);
    };

    const handleTemplateClick = (lab: Lab) => {
        setCurrentLab(lab);
        setIsTemplateDialogOpen(true);
    };

    const handleTemplateSave = () => {
        setIsTemplateDialogOpen(false);
        refetch();
    };

    const handleLabAdded = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:LAB_ADDED_MESSAGE'),
        });
        setIsAddDialogOpen(false);
        refetch();
    };

    const handleLabModified = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:LAB_UPDATED_MESSAGE'),
        });
        setIsEditDialogOpen(false);
        refetch();
    };

    const handleTransportSettingsSave = () => {
        setIsTransportDialogOpen(false);
        refetch();
    };

    const handleHHSyncClicked = (lab: Lab) => {
        setCurrentLab(lab);
        setIsHHSyncDialogOpen(true);
    };

    const handleLabSynced = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:ORGANIZATION_SYNCED_HIT_HORIZONS'),
        });
        setIsHHSyncDialogOpen(false);
        refetch();
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('common:LABS')} />
            <LabPageActions onFilterChange={handleFilterChange} onAddLabDialogOpen={() => setIsAddDialogOpen(true)} />
            {loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.refetch ? (
                <Loader />
            ) : (
                <Fragment>
                    <LabList
                        filteredLabs={labs}
                        onFetchMore={handleFetchMore}
                        onSetOrderOnField={setOrderOnField}
                        orderBy={orderBy}
                        onEditLabClick={handleLabEditClick}
                        onTransportSettingClick={handleTransportSettingsClick}
                        onHHSyncClick={handleHHSyncClicked}
                        onTemplateClick={handleTemplateClick}
                    />
                    <AddLabDialog
                        dialogOpen={isAddDialogOpen}
                        onCloseDialog={handleCloseDialog}
                        onLabAdded={handleLabAdded}
                    />
                    <EditLabDialog
                        dialogOpen={isEditDialogOpen}
                        lab={currentLab}
                        onCloseDialog={handleCloseDialog}
                        onLabModified={handleLabModified}
                    />
                    {currentLab?._id && isTransportDialogOpen ? (
                        <TransportSettingsDialog
                            labId={currentLab._id}
                            dialogOpen={isTransportDialogOpen}
                            onCloseDialog={handleCloseDialog}
                            onSave={handleTransportSettingsSave}
                        />
                    ) : undefined}

                    <SyncHitHoriozonsDialog
                        organization={currentLab as Organization}
                        dialogOpen={isHHSyncDialogOpen}
                        onCloseDialog={handleCloseDialog}
                        onOrganizationModified={handleLabSynced}
                    />
                    {currentLab?._id && isTemplateDialogOpen ? (
                        <PdfTemplateDialog
                            labId={currentLab._id}
                            open={isTemplateDialogOpen}
                            onCloseDialog={handleCloseDialog}
                            onSave={handleTemplateSave}
                        />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default AdminLabs;
